// PKCE (RFC 7636) for the embed handshake.
//
// Embedded, up-spa is a public OAuth client: it ships in a browser and can hold
// no secret, so it cannot prove who it is when redeeming a code. PKCE gives it a
// per-handshake secret instead. The verifier is generated here, stays here, and
// leaves exactly once — to Spectrum's token endpoint, alongside the code.
//
// What crosses the browser is only the challenge, its SHA-256 hash. That is why
// a code intercepted between Zurich's page and this frame is inert: redeeming it
// needs the verifier, which was never in a message.

export type PkcePair = {
  /** Secret. Never logged, never posted, never stored outside memory. */
  verifier: string;
  /** SHA-256 of the verifier, base64url. Public by design. */
  challenge: string;
};

const base64url = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// 32 bytes → 43 base64url characters, comfortably inside the 43–128 the RFC
// permits and at the length it recommends.
const VERIFIER_BYTES = 32;

export const createPkcePair = async (): Promise<PkcePair> => {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(VERIFIER_BYTES)));
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64url(new Uint8Array(digest)) };
};
